import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import i18n from "../i18n";
import { getFingerprint } from "../lib/fingerprint";

const AuthContext = createContext(null);

// Rank tiers low → high; a move to a higher index is a promotion worth celebrating.
const RANK_ORDER = ["Omega", "Delta", "Beta", "Alpha"];

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=unauth, obj=auth
  const [ready, setReady] = useState(false);

  // Keep the UI language in sync with the wolf's saved preference.
  useEffect(() => {
    const lng = user && user.language;
    if (lng && ["en", "es", "fr"].includes(lng) && i18n.language !== lng) {
      i18n.changeLanguage(lng);
    }
  }, [user && user.language]);

  useEffect(() => {
    // Session lives in an httpOnly cookie; ask the server who we are.
    api.get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(false))
      .finally(() => setReady(true));
  }, []);

  // Report the wolf's device timezone so Mystic can orient to their local time.
  useEffect(() => {
    if (!user || !user.id) return;
    let tz = null;
    try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { tz = null; }
    if (!tz || tz === user.tz) return;
    const offset = -new Date().getTimezoneOffset();
    api.post("/auth/timezone", { tz, offset }).catch(() => {});
  }, [user && user.id, user && user.tz]);

  // Report screen + timezone once per browser session (Alpha-only Connection Info in Pack CRM).
  useEffect(() => {
    if (!user || !user.id) return;
    const k = `client_info_sent_${user.id}`;
    if (sessionStorage.getItem(k)) return;
    let tz = null;
    try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { tz = null; }
    getFingerprint().then((fingerprint) => api.post("/me/client-info", {
      fingerprint,
      screen: `${window.screen.width}x${window.screen.height}`,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      pixel_ratio: window.devicePixelRatio || 1,
      timezone: tz,
      touch_points: navigator.maxTouchPoints || 0,
    })).then(() => sessionStorage.setItem(k, "1")).catch(() => {});
  }, [user && user.id]);

  // Celebrate a rank-up: compare the live rank to the last one we saw for this wolf. On a promotion,
  // flag it (the membership card plays the restyle reveal) and nudge them to go look.
  useEffect(() => {
    if (!user || !user.id || !user.rank) return;
    const seenKey = `rank_seen_${user.id}`;
    const prev = localStorage.getItem(seenKey);
    if (prev && prev !== user.rank && RANK_ORDER.indexOf(user.rank) > RANK_ORDER.indexOf(prev)) {
      localStorage.setItem(`rankup_celebrate_${user.id}`, user.rank);
      toast.success(`You've risen to ${user.rank}! 🐺`, {
        description: "Your membership card has a brand-new look.",
        duration: 9000,
        action: { label: "See my card", onClick: () => window.location.assign("/profile?view=card") },
      });
    }
    localStorage.setItem(seenKey, user.rank);
  }, [user && user.id, user && user.rank]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.requires_password_change) return data; // caller must set a new password first
    if (data.requires_2fa) return data;             // caller must complete the 2FA challenge
    setUser(data.user);
    return data;
  }, []);

  const verify2fa = useCallback(async (challenge_token, code, trust_device = false, device_label = null) => {
    const { data } = await api.post("/auth/2fa/verify", { challenge_token, code, trust_device, device_label });
    setUser(data.user);
    return data;
  }, []);

  const loginWithPasskey = useCallback(async () => {
    const { fromB64url, toB64url } = await import("../lib/webauthn");
    const { data: opts } = await api.post("/auth/passkey/login/begin");
    const publicKey = {
      ...opts,
      challenge: fromB64url(opts.challenge),
      allowCredentials: (opts.allowCredentials || []).map((c) => ({ ...c, id: fromB64url(c.id) })),
    };
    const cred = await navigator.credentials.get({ publicKey });
    const r = cred.response;
    const payload = {
      id: cred.id, rawId: toB64url(cred.rawId), type: cred.type,
      clientExtensionResults: cred.getClientExtensionResults(),
      response: {
        authenticatorData: toB64url(r.authenticatorData),
        clientDataJSON: toB64url(r.clientDataJSON),
        signature: toB64url(r.signature),
        userHandle: r.userHandle ? toB64url(r.userHandle) : null,
      },
    };
    const { data } = await api.post("/auth/passkey/login/complete", payload);
    setUser(data.user);
    return data;
  }, []);

  const changeTempPassword = useCallback(async (change_token, password) => {
    const { data } = await api.post("/auth/change-temp-password", { change_token, password });
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (payload) => {
    const fingerprint = await getFingerprint();
    const { data } = await api.post("/auth/register", { ...payload, fingerprint });
    if (data.requires_verification) return data; // unverified — caller shows "check your email"
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch { /* clear session regardless */ }
    setUser(false);
  }, []);

  const signOutEverywhere = useCallback(async () => {
    await api.post("/security/sign-out-everywhere");
    setUser(false);
  }, []);

  const refreshUser = useCallback(async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
    return data;
  }, []);

  // Session auto-renew: /auth/me re-mints the 7-day sliding token. Quietly ping it on genuine
  // activity (at most once every 30 min) so an active wolf's session keeps extending and the
  // 5-min expiry warning only ever appears for a truly idle tab.
  useEffect(() => {
    if (!user || user === false) return;
    let last = Date.now();
    const THROTTLE = 30 * 60 * 1000;
    const onActivity = () => {
      if (document.visibilityState === "hidden") return;
      if (Date.now() - last < THROTTLE) return;
      last = Date.now();
      api.get("/auth/me").then((r) => setUser(r.data)).catch(() => {});
    };
    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    return () => events.forEach((e) => window.removeEventListener(e, onActivity));
  }, [user && user.id]);

  const value = useMemo(
    () => ({ user, setUser, ready, login, register, logout, signOutEverywhere, refreshUser, changeTempPassword, verify2fa, loginWithPasskey }),
    [user, ready, login, register, logout, signOutEverywhere, refreshUser, changeTempPassword, verify2fa, loginWithPasskey]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
